import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { hooks as h } from "@/hooks";
import { ui } from "@patient-watch/ui";
import { Button } from "@patient-watch/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@patient-watch/ui/table";
import { Loader2, Plus } from "lucide-react";
import { useState } from "react";
import { useCreateDictionary, useDictionarySummaries } from "@/features/dictionaries/api/dictionaryBuilderApi";
import { useAuth } from "@/utils/auth";

const DictionariesBuilder = () => {
    const auth = useAuth();
    const isAdmin = Boolean(auth?.roles?.isAdmin || auth?.isSuperUser);
    const navigate = useNavigate();
    const { data: dictionaries, isLoading } = useDictionarySummaries();
    const createDictionary = useCreateDictionary();
    const [query, setQuery] = useState("");

    h.useNav([
        { label: "Builder", route: { to: "/builder" } },
        { label: "Dictionaries", route: { to: "/builder/dictionaries" } },
    ]);

    const filtered = (dictionaries ?? []).filter((dictionary) =>
        `${dictionary.name} ${dictionary.description ?? ""}`.toLowerCase().includes(query.trim().toLowerCase())
    );

    return (
        <div className="space-y-6 p-2 md:p-4">
            <div className="flex justify-between items-center gap-4">
                <div>
                    <h1 className="font-semibold text-3xl tracking-tight">Dictionaries</h1>
                    <p className="text-muted-foreground">
                        Shared answer lists. A form question uses one by picking it under &quot;Choices from&quot;, so
                        every form that uses the list stays in step.
                    </p>
                </div>
                {isAdmin ? (
                    <Button
                        size="sm"
                        disabled={createDictionary.isPending}
                        onClick={() =>
                            createDictionary.mutate("New dictionary", {
                                onSuccess: (dictionaryId) =>
                                    void navigate({
                                        to: "/builder/dictionaries/$dictionaryId",
                                        params: { dictionaryId },
                                    }),
                            })
                        }
                    >
                        <Plus className="mr-2 w-4 h-4" />
                        New dictionary
                    </Button>
                ) : null}
            </div>

            <ui.Input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search dictionaries..."
                className="max-w-sm"
            />

            <ui.Card>
                {isLoading ? (
                    <div className="flex justify-center items-center py-12">
                        <Loader2 className="w-8 h-8 text-muted-foreground animate-spin" />
                    </div>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Name</TableHead>
                                <TableHead>Description</TableHead>
                                <TableHead className="text-right">Options</TableHead>
                                <TableHead className="text-right">Used by</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {filtered.length ? (
                                filtered.map((dictionary) => (
                                    <TableRow key={dictionary.dictionary_id}>
                                        <TableCell className="font-medium">
                                            <Link
                                                to="/builder/dictionaries/$dictionaryId"
                                                params={{ dictionaryId: dictionary.dictionary_id }}
                                                className="hover:underline"
                                            >
                                                {dictionary.name}
                                            </Link>
                                        </TableCell>
                                        <TableCell className="text-muted-foreground text-sm">
                                            {dictionary.description}
                                        </TableCell>
                                        <TableCell className="text-sm text-right tabular-nums">
                                            {dictionary.option_count}
                                        </TableCell>
                                        <TableCell className="text-sm text-right tabular-nums">
                                            {dictionary.question_count === 1
                                                ? "1 question"
                                                : `${dictionary.question_count} questions`}
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={4} className="py-12 text-muted-foreground text-center">
                                        No dictionaries match.
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                )}
            </ui.Card>
        </div>
    );
};

export const Route = createFileRoute("/_app/builder/dictionaries/")({
    component: DictionariesBuilder,
});
